import type React from 'react'
import { useState, useRef, useEffect } from 'react'
import styled from 'styled-components'

interface ITextInputContainer {
  focused: boolean
  disabled?: boolean
  error: boolean
}

export interface Props {
  type?: string
  label?: string
  onChange: Function
  name?: string
  required?: boolean
  value: any
  disabled?: boolean
  maxLength?: number
  minLength?: number
  onError?: Function
  className?: string
  'aria-labelledby'?: string
}

const TextInputContainer = styled.div<ITextInputContainer>`
  background: ${props => (props.disabled ? '#f2f2f2' : 'white')};
  border-style: solid;
  border-color: rgba(188, 190, 192, 0.5);
  border-width: 1px;
  padding: 18px;
  border-radius: 5px;
  position: relative;
  text-align: left;
  cursor: text;
  border-color: ${(props: ITextInputContainer) => getColour(props)};
`

function getColour({ focused, error }: ITextInputContainer) {
  if (error) {
    return '#ed1c24'
  }
  if (focused) {
    return '#34bebd'
  }

  return '#55616b'
}

const TextAreInputElement = styled.textarea`
  resize: none;
  outline: none;
  border: none;
  background: none;
  width: 100%;
  height: 100%;
  font-family: 'Lato', sans-serif;

  &:disabled {
    background: #f2f2f2;
  }
`

const TextAreaInput: React.FC<Props> = ({
  label = '',
  onChange,
  name = '',
  required,
  type = 'text',
  value,
  disabled = false,
  maxLength,
  minLength,
  onError = () => {},
  className,
  'aria-labelledby': ariaLabelledBy,
}) => {
  const [errors, setErrors] = useState<string[]>([])
  const [showErrors, setShowErrors] = useState<boolean>(false)
  const [focused, setFocused] = useState(false)
  const inputRef = useRef<HTMLTextAreaElement>(document.createElement('textarea'))

  function handleBlur() {
    if (disabled) return
    if (inputRef.current.value.trim() === '') {
      setFocused(false)
    } else {
      setFocused(true)
      setShowErrors(true)
    }
  }

  function handleFocus() {
    if (disabled) return

    inputRef.current.focus()
    setFocused(true)
  }

  useEffect(() => {
    if (inputRef.current.value.trim() !== '') {
      setFocused(true)
    } else {
      setFocused(false)
    }

    const timer = setTimeout(() => {
      if (value !== '') {
        setShowErrors(true)
      }
    }, 2000) // miliseconds to displaying errors

    runValidation()

    return () => {
      clearTimeout(timer)
    }
  }, [value]) // eslint-disable-line react-hooks/exhaustive-deps

  function runValidation() {
    const foundErrors: string[] = []
    if (type === 'email') {
      if (!value.includes('@') || !value.includes('.') || value.length < 6) {
        foundErrors.push('Email address is not correctly formatted')
      }
    }

    if (minLength && minLength > value.length) {
      foundErrors.push('Not enough characters')
    }

    if (required && value.length === 0) {
      foundErrors.push('This field is required')
    }

    setErrors(foundErrors)

    if (onError) {
      onError(foundErrors)
    }
  }

  return (
    <TextInputContainer
      className={className}
      error={errors.length > 0 && showErrors}
      disabled={disabled}
      focused={focused}
      onClick={() => handleFocus()}
    >
      <TextAreInputElement
        placeholder={label}
        ref={inputRef}
        name={name}
        maxLength={maxLength}
        disabled={disabled}
        required={required}
        value={value || ''}
        onClick={() => setFocused(true)}
        onBlur={() => handleBlur()}
        onChange={e => onChange(e)}
        onFocus={() => handleFocus()}
        onBeforeInputCapture={() => handleFocus()}
        tabIndex={0}
        {...{ 'aria-labelledby': ariaLabelledBy }}
      />
    </TextInputContainer>
  )
}

export default TextAreaInput
